import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { ResponseError } from '@clickersphere/core/entities/ResponseError';
import { ContractVariableSourceEnum } from '@clickersphere/core/enums/ContractVariableSourceEnum';
import {
  IContractPreview,
  IIssueContractParams,
} from '@clickersphere/core/services/LeadContractService';
import { formatCurrency } from '@clickersphere/core/utils/formatCurrency';
import {
  LeadLegalField,
  leadLegalVariableField,
} from '@clickersphere/core/utils/leadLegalVariableField';
import { useContractTemplates } from '../../../../app/hooks/contract-template/useContractTemplates';
import { useContractors } from '../../../../app/hooks/contractor/useContractors';
import { useLeadLegalInfo } from '../../../../app/hooks/lead/useLeadLegalInfo';
import { useUpsertLeadLegalInfo } from '../../../../app/hooks/lead/useUpsertLeadLegalInfo';
import { useLeadSales } from '../../../../app/hooks/lead-sale/useLeadSales';
import { useIssueContract } from '../../../../app/hooks/lead-contract/useIssueContract';
import { usePreviewContract } from '../../../../app/hooks/lead-contract/usePreviewContract';
import { useError } from '../../../../app/hooks/useError';
import useToast from '../../../../app/hooks/useToast';
import { useStore } from '../../../../app/store';

type IssueContractStep = 'form' | 'preview';

export type InlineLegalField = Exclude<LeadLegalField, 'address'>;

const CUSTOM_VALUE_SOURCES = [ContractVariableSourceEnum.CUSTOM];

export function useIssueContractModalController() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { handleError } = useError();

  const { issueContractLeadId, handleCloseModal } = useStore(
    useShallow((state) => ({
      issueContractLeadId: state.modal.issueContractLeadId,
      handleCloseModal: state.modal.handleCloseModal,
    })),
  );

  const [step, setStep] = useState<IssueContractStep>('form');
  const [templateId, setTemplateId] = useState('');
  const [contractorId, setContractorId] = useState('');
  const [saleId, setSaleId] = useState('');
  const [issuanceCity, setIssuanceCity] = useState('');
  const [customValues, setCustomValues] = useState<Record<string, string>>({});
  const [legalValues, setLegalValues] = useState<
    Partial<Record<InlineLegalField, string>>
  >({});
  const [templateError, setTemplateError] = useState<string | undefined>(
    undefined,
  );
  const [contractorError, setContractorError] = useState<string | undefined>(
    undefined,
  );
  const [preview, setPreview] = useState<IContractPreview | null>(null);

  const { contractTemplates, isContractTemplatesLoading } =
    useContractTemplates();
  const { contractors, isContractorsLoading } = useContractors();
  const { sales } = useLeadSales(issueContractLeadId ?? '');
  const { leadLegalInfo } = useLeadLegalInfo(issueContractLeadId ?? '');
  const { upsertLeadLegalInfoAsync, upsertLeadLegalInfoIsPending } =
    useUpsertLeadLegalInfo();
  const { previewContractAsync, previewContractIsPending } =
    usePreviewContract();
  const { issueContractAsync, issueContractIsPending } = useIssueContract();

  const activeTemplates = useMemo(
    () => contractTemplates.filter((template) => template.active),
    [contractTemplates],
  );

  const selectedTemplate = useMemo(
    () => activeTemplates.find((template) => template.id === templateId),
    [activeTemplates, templateId],
  );

  const customFields = useMemo(
    () =>
      selectedTemplate?.variablesSchema.filter((variable) =>
        CUSTOM_VALUE_SOURCES.includes(variable.source),
      ) ?? [],
    [selectedTemplate],
  );

  const templateOptions = useMemo(
    () =>
      activeTemplates.map((template) => ({
        value: template.id,
        text: template.name,
      })),
    [activeTemplates],
  );

  const contractorOptions = useMemo(
    () =>
      contractors.map((contractor) => ({
        value: contractor.id,
        text: contractor.name,
      })),
    [contractors],
  );

  const saleOptions = useMemo(
    () =>
      [...(sales ?? [])]
        .sort(
          (a, b) => new Date(b.soldAt).getTime() - new Date(a.soldAt).getTime(),
        )
        .map((sale) => ({
          value: sale.id,
          text: `${format(new Date(sale.soldAt), 'dd MMM yyyy')} — ${formatCurrency(
            sale.totalPrice,
            i18n.language,
          )}`,
        })),
    [sales, i18n.language],
  );

  const hasMultipleSales = saleOptions.length > 1;

  const missingByKey = useMemo(() => {
    const map = new Map<string, ContractVariableSourceEnum>();
    preview?.missing.forEach((missing) => {
      map.set(missing.key, missing.source);
    });
    return map;
  }, [preview]);

  const hasMissingVariables = (preview?.missing.length ?? 0) > 0;

  const missingLegalFields = useMemo(() => {
    const fields = new Set<InlineLegalField>();
    preview?.missing
      .filter(
        (missing) => missing.source === ContractVariableSourceEnum.LEAD_LEGAL,
      )
      .forEach((missing) => {
        const field = leadLegalVariableField(missing.key);
        if (field && field !== 'address') fields.add(field);
      });
    return [...fields];
  }, [preview]);

  const hasMissingAddress = useMemo(
    () =>
      (preview?.missing ?? []).some(
        (missing) =>
          missing.source === ContractVariableSourceEnum.LEAD_LEGAL &&
          leadLegalVariableField(missing.key) === 'address',
      ),
    [preview],
  );

  const missingAnchorVariables = useMemo(
    () =>
      selectedTemplate?.variablesSchema.filter(
        (variable) =>
          variable.required && (variable.anchors?.length ?? 0) === 0,
      ) ?? [],
    [selectedTemplate],
  );

  const missingAnchorNames = missingAnchorVariables
    .map((variable) => variable.label)
    .join(', ');

  const hasMissingAnchors = missingAnchorVariables.length > 0;

  function handleTemplateChange(value: string) {
    setTemplateId(value);
    setTemplateError(undefined);
    setCustomValues({});
  }

  function handleContractorChange(value: string) {
    setContractorId(value);
    setContractorError(undefined);
  }

  function handleSaleChange(value: string) {
    setSaleId(value);
  }

  function handleCustomValueChange(key: string, value: string) {
    setCustomValues((old) => ({ ...old, [key]: value }));
  }

  function buildPayload(): IIssueContractParams {
    const filledCustomValues = Object.fromEntries(
      Object.entries(customValues).filter(([, value]) => value.trim() !== ''),
    );

    return {
      templateId,
      contractorId,
      saleId: saleId || undefined,
      issuanceCity: issuanceCity.trim() || undefined,
      customValues:
        Object.keys(filledCustomValues).length > 0
          ? filledCustomValues
          : undefined,
    };
  }

  function validateForm(): boolean {
    let valid = true;

    if (!templateId) {
      setTemplateError(t('contract.issue.validation.templateRequired'));
      valid = false;
    }

    if (!contractorId) {
      setContractorError(t('contract.issue.validation.contractorRequired'));
      valid = false;
    }

    return valid;
  }

  async function handleContinue() {
    if (!issueContractLeadId || !validateForm()) return;

    try {
      const result = await previewContractAsync({
        leadId: issueContractLeadId,
        data: buildPayload(),
      });
      setPreview(result);
      setStep('preview');
    } catch (error) {
      if (error instanceof ResponseError) {
        handleError(error);
        return;
      }
      toast(t('contract.issue.toast.previewError'), 'error');
    }
  }

  function handleBack() {
    setStep('form');
    setPreview(null);
  }

  function handleLegalValueChange(field: InlineLegalField, value: string) {
    setLegalValues((old) => ({ ...old, [field]: value }));
  }

  async function handleSaveMissingLegalInfo() {
    if (!issueContractLeadId) return;

    const filled = Object.entries(legalValues).filter(
      ([, value]) => value.trim() !== '',
    );
    if (filled.length === 0) return;

    try {
      await upsertLeadLegalInfoAsync({
        leadId: issueContractLeadId,
        data: {
          cpf: legalValues.cpf?.trim() || leadLegalInfo?.cpf || undefined,
          rg: legalValues.rg?.trim() || leadLegalInfo?.rg || undefined,
          profession:
            legalValues.profession?.trim() ||
            leadLegalInfo?.profession ||
            undefined,
          maritalStatus:
            legalValues.maritalStatus?.trim() ||
            leadLegalInfo?.maritalStatus ||
            undefined,
          nationality:
            legalValues.nationality?.trim() ||
            leadLegalInfo?.nationality ||
            undefined,
          address: leadLegalInfo?.address ?? null,
        },
      });

      const result = await previewContractAsync({
        leadId: issueContractLeadId,
        data: buildPayload(),
      });
      setPreview(result);
      setLegalValues({});
      toast(t('contract.issue.toast.legalInfoSaved'), 'success');
    } catch (error) {
      if (error instanceof ResponseError) {
        handleError(error);
        return;
      }
      toast(t('contract.issue.toast.legalInfoError'), 'error');
    }
  }

  async function handleIssue() {
    if (!issueContractLeadId || hasMissingVariables) return;

    try {
      const issued = await issueContractAsync({
        leadId: issueContractLeadId,
        data: buildPayload(),
      });

      if (issued.pdfGenerated) {
        toast(t('contract.issue.toast.issued'), 'success');
      } else {
        toast(t('contract.issue.toast.pdfFailed'), 'warning');
      }

      handleCloseModal();
    } catch (error) {
      if (error instanceof ResponseError) {
        handleError(error);
        return;
      }
      toast(t('contract.issue.toast.error'), 'error');
    }
  }

  const isLoadingOptions = isContractTemplatesLoading || isContractorsLoading;

  return {
    t,
    step,
    templateId,
    contractorId,
    saleId,
    saleOptions,
    hasMultipleSales,
    handleSaleChange,
    issuanceCity,
    customValues,
    templateError,
    contractorError,
    selectedTemplate,
    customFields,
    templateOptions,
    contractorOptions,
    preview,
    missingByKey,
    hasMissingVariables,
    missingLegalFields,
    hasMissingAddress,
    legalValues,
    handleLegalValueChange,
    handleSaveMissingLegalInfo,
    isSavingLegalInfo: upsertLeadLegalInfoIsPending,
    missingAnchorNames,
    hasMissingAnchors,
    isLoadingOptions,
    hasActiveTemplates: activeTemplates.length > 0,
    hasContractors: contractors.length > 0,
    isPreviewing: previewContractIsPending,
    isIssuing: issueContractIsPending,
    handleTemplateChange,
    handleContractorChange,
    handleIssuanceCityChange: setIssuanceCity,
    handleCustomValueChange,
    handleContinue,
    handleBack,
    handleIssue,
    handleCloseModal,
  };
}
